// src/pages/Policies.jsx
import React, { useEffect, useState } from "react";
import { getPolicies } from "../csrApi";
import heroBg from "../assets/Policy.jpg"; // swap for a dedicated Policies hero image if you have one
import { FiX } from "react-icons/fi";
import "../styles/Gallery.css";

// A category counts as "Annual Return" if its name starts with this prefix.
// Anything else (HR-Policy, CSR Policy, or any future custom category you
// add) falls under the "Policies" tab automatically.
const isAnnualReturn = (category) => category?.startsWith("Annual Return");

// Backend serves uploaded files from this origin (port 8000 by default).
const MEDIA_BASE = process.env.REACT_APP_API_BASE_URL;
const mediaUrl = (path) => (path?.startsWith("http") ? path : `${MEDIA_BASE}${path}`);

/* ── Secure, view-only PDF modal (no download, watermarked) ── */
const PDFViewerModal = ({ doc, onClose }) => {
  if (!doc) return null;

  return (
    <>
      <style>{`
        @media print {
          body { display: none !important; }
        }
        .pdf-iframe-wrapper {
          position: relative;
          flex: 1;
          overflow: hidden;
        }
        .pdf-iframe-wrapper iframe {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
        }
        .watermark-layer {
          position: absolute;
          inset: 0;
          z-index: 20;
          pointer-events: none;
          overflow: hidden;
        }
      `}</style>

      <div
        className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <button
          className="absolute top-4 right-4 text-white text-3xl"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <FiX />
        </button>

        <div
          className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "90vw",
            maxWidth: "1000px",
            height: "85vh",
            userSelect: "none",
          }}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div>
              <p className="font-bold text-gray-900 text-sm">{doc.title}</p>
              {doc.description && (
                <p className="text-xs text-gray-500">{doc.description}</p>
              )}
            </div>
            {/* <a
              href={mediaUrl(doc.fileUrl)}
              download
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex-shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download
            </a> */}
          </div>

          <div
            className="pdf-iframe-wrapper"
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="watermark-layer">
              {Array.from({ length: 25 }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    position: "absolute",
                    top: `${(i % 5) * 22}%`,
                    left: `${Math.floor(i / 5) * 28 - 10}%`,
                    transform: "rotate(-35deg)",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "rgba(30, 58, 110, 0.12)",
                    whiteSpace: "nowrap",
                    letterSpacing: "2px",
                    pointerEvents: "none",
                  }}
                >
                  LAXMI CIVIL ENGINEERING SERVICES PVT. LTD.
                </span>
              ))}
            </div>

            <iframe
              src={`${mediaUrl(doc.fileUrl)}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=page-fit&page=1`}
              title={doc.title}
            />
          </div>

          <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <p className="text-xs text-gray-400 text-center select-none">
              🔒 Printing from this viewer is disabled — use the Download button above to save a copy.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Lightweight image lightbox, matching the one used on the CSR page ── */
// const ImageLightbox = ({ src, onClose }) => {
//   if (!src) return null;

//   const [shareStatus, setShareStatus] = useState("");
const ImageLightbox = ({ src, onClose }) => {
  const [shareStatus, setShareStatus] = useState("");

  if (!src) return null;
  const handleShare = async (e) => {
    e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({ url: src });
      } else {
        await navigator.clipboard.writeText(src);
        setShareStatus("Link copied!");
        setTimeout(() => setShareStatus(""), 2000);
      }
    } catch {
      // user cancelled the share sheet — nothing to do
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 text-white text-3xl font-bold"
        >
          ×
        </button>
        <img src={src} alt="Preview" className="max-w-[90vw] max-h-[90vh] object-contain" />
        <button
          onClick={handleShare}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/90 hover:bg-white text-gray-800 shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          {shareStatus || "Share"}
        </button>
      </div>
    </div>
  );
};

/* ── Card for the "Policies" tab (icon + title, no photo) ── */
const DocCard = ({ doc, onOpen }) => (
  <div
    className="rounded-xl shadow-lg overflow-hidden border bg-white hover:shadow-2xl transition-all duration-300 cursor-pointer"
    onClick={() => doc.fileUrl && onOpen(doc)}
  >
    <div className="h-40 w-full bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14 text-white opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    </div>
    <div className="p-5 text-left">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-semibold text-gray-900">{doc.title}</p>
        {doc.badge && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 ml-2 flex-shrink-0">
            {doc.badge}
          </span>
        )}
      </div>
      {doc.description && (
        <p className="text-sm text-gray-500 leading-relaxed">{doc.description}</p>
      )}
      <p className="text-xs text-blue-600 font-medium mt-3">
        {doc.fileUrl ? "Click to view" : "No PDF attached"}
      </p>
    </div>
  </div>
);

/* ── Card for the "Annual Return" tab (photo + optional PDF) ──
   Clicking the photo opens an image preview; the PDF is opened only via
   the explicit "View PDF" link, so the two actions no longer collide. */
const AnnualReturnCard = ({ doc, onOpenPdf, onOpenImage }) => (
  <div className="rounded-xl shadow-lg overflow-hidden border bg-white hover:shadow-2xl transition-all duration-300">
    {doc.photo ? (
      <img
        src={mediaUrl(doc.photo)}
        alt={doc.title}
        className="h-64 w-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
        onClick={() => onOpenImage(mediaUrl(doc.photo))}
        draggable="false"
        onDragStart={(e) => e.preventDefault()}
      />
    ) : (
      <div
        className="h-64 w-full bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center cursor-pointer"
        onClick={() => doc.fileUrl && onOpenPdf(doc)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14 text-white opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>
    )}
    <div className="p-5 text-left">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-semibold text-gray-900">{doc.title}</p>
        {doc.badge && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 ml-2 flex-shrink-0">
            {doc.badge}
          </span>
        )}
      </div>
      {doc.description && (
        <p className="text-sm text-gray-500 leading-relaxed">{doc.description}</p>
      )}
      {doc.fileUrl ? (
        <button
          onClick={() => onOpenPdf(doc)}
          className="text-xs text-blue-600 font-semibold mt-3 hover:text-blue-800"
        >
          View PDF →
        </button>
      ) : (
        <p className="text-xs text-gray-400 mt-3">No PDF attached</p>
      )}
    </div>
  </div>
);

/* ── Main page ── */
const Policies = () => {
  const [activeTab, setActiveTab] = useState("policies"); // "policies" | "annual"
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Disable right-click, matching Gallery.js
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  useEffect(() => {
    getPolicies()
      .then((res) => setPolicies(Array.isArray(res.data) ? res.data : []))
      .finally(() => setLoading(false));
  }, []);

  // Block Ctrl+P / Ctrl+S while this page is open
  useEffect(() => {
    const blockKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        alert("Printing is disabled for this page.");
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", blockKeys);
    return () => window.removeEventListener("keydown", blockKeys);
  }, []);

  const policyDocs = policies.filter((p) => !isAnnualReturn(p.category));
  const annualDocs = policies.filter((p) => isAnnualReturn(p.category));

  const policyCategories = Array.from(new Set(policyDocs.map((p) => p.category)));
  const annualCategories = Array.from(new Set(annualDocs.map((p) => p.category)));

  const tabs = [
    { label: "Policies", value: "policies" },
    { label: "Annual Return", value: "annual" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {selectedDoc && (
        <PDFViewerModal doc={selectedDoc} onClose={() => setSelectedDoc(null)} />
      )}
      {lightboxImage && (
        <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center h-[60vh] flex items-center justify-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 text-center px-4 animate-fadeIn">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg">
            Policies &amp; Annual Returns
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white mt-2 italic drop-shadow-md">
            Transparency in Every Document
          </p>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex flex-wrap justify-center gap-4 px-4 mt-8 mb-10">
        {tabs.map((t) => {
          const isSelected = activeTab === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setActiveTab(t.value)}
              className={`text-base lg:text-lg text-white font-semibold px-6 py-3 rounded-2xl shadow-lg transition-all duration-300 transform hover:scale-105
                ${isSelected ? "bg-gradient-to-r from-blue-600 to-blue-800 ring-4 ring-offset-2 ring-white" : "bg-gradient-to-r from-gray-600 to-gray-800"}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="px-6 pb-16">
        {loading ? (
          <p className="text-gray-500 text-xl text-center">Loading documents...</p>
        ) : activeTab === "policies" ? (
          policyDocs.length === 0 ? (
            <p className="text-gray-600 text-xl text-center">No policy documents added yet.</p>
          ) : (
            policyCategories.map((cat) => (
              <div key={cat} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{cat}</h2>
                <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
                  {policyDocs
                    .filter((p) => p.category === cat)
                    .map((doc) => (
                      <DocCard key={doc.id} doc={doc} onOpen={setSelectedDoc} />
                    ))}
                </div>
              </div>
            ))
          )
        ) : annualDocs.length === 0 ? (
          <p className="text-gray-600 text-xl text-center">No annual return documents added yet.</p>
        ) : (
          annualCategories.map((cat) => (
            <div key={cat} className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{cat}</h2>
              <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
                {annualDocs
                  .filter((p) => p.category === cat)
                  .map((doc) => (
                    <AnnualReturnCard
                      key={doc.id}
                      doc={doc}
                      onOpenPdf={setSelectedDoc}
                      onOpenImage={setLightboxImage}
                    />
                  ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Policies;